import React, { useState } from "react";
import { Column } from "@amzn/meridian";
import CaptureButton from "../../inspection/components/CaptureButton";
import ZoomControl from "../../inspection/components/ZoomControl";
import { captureVideoFrame } from "../../inspection/utils/videoUtils";
import { useCamera } from "src/hooks/useCamera";
import "./SimpleCamera.scss";

interface SimpleCameraProps {
  onCapture: (file: File) => void;
  onCancel?: () => void;
  screenWidth?: number;
  screenHeight?: number;
}

const SimpleCamera: React.FC<SimpleCameraProps> = ({
  onCapture,
  onCancel,
  screenWidth = 1920,
  screenHeight = 1080,
}) => {
  const { videoRef, isReady, error, setError, stopCamera } = useCamera({ width: screenWidth, height: screenHeight });
  const [isCapturing, setIsCapturing] = useState(false);

  const handleCapture = async () => {
    if (!videoRef.current || !isReady) return;

    setIsCapturing(true);

    try {
      const capturedFile = await captureVideoFrame(videoRef.current);
      stopCamera();
      onCapture(capturedFile);
    } catch (error) {
      console.error("Failed to capture frame:", error);
      setError("Failed to capture image. Please try again.");
      setIsCapturing(false);
    }
  };

  const handleCancel = () => {
    stopCamera();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Column className="simple-camera-viewport pwa-fullscreen-viewport">
      {/* Video Preview */}
      <video
        ref={videoRef}
        className="simple-camera-video"
        autoPlay
        muted
        playsInline
      />

      {/* Camera Frame Wrapper */}
      <div className="simple-camera-frame-wrapper">
        {isReady && (
          <Column className="simple-camera-frame">
            <Column className="simple-camera-top-line" />
            <Column className="simple-camera-left-line" />
            <Column className="simple-camera-right-line" />
            <Column className="simple-camera-bottom-line" />
          </Column>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="simple-camera-error">
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {!isReady && !error && (
        <div className="simple-camera-loading">
          <p>Initializing camera...</p>
        </div>
      )}

      {/* Capture Button */}
      {isReady && !error && (
        <>
          <CaptureButton 
            onClick={handleCapture}
            disabled={isCapturing}
          />
          <ZoomControl videoRef={videoRef} />
        </>
      )}

      {/* Cancel Button - Top Left Corner */}
      {onCancel && (
        <div className="simple-camera-cancel-wrapper">
          <button
            className="simple-camera-cancel-button"
            onClick={handleCancel}
            disabled={isCapturing}
          >
            ✕
          </button>
        </div>
      )}
    </Column>
  );
};

export default SimpleCamera;
